import { cn } from '@yeshuman/sdk'

import { yeshumanAnn, yeshumanAnnDim, yeshumanEditorialSm, yeshumanEditorialTerm, yeshumanUiSans } from './typography'
import { WORKING_SESSION_LAYERS } from './working-session'
import type { WorkingSessionTable } from './working-session-api'

function BriefBlock({
  label,
  value,
  loud = false,
  muted = false,
  packet = false,
}: {
  label: string
  value: string
  loud?: boolean
  muted?: boolean
  packet?: boolean
}) {
  if (!value && (packet || !loud)) return null
  return (
    <section className="space-y-2">
      <h3 className={cn(yeshumanAnn, muted ? yeshumanAnnDim : '')}>{label}</h3>
      <p
        className={cn(
          yeshumanUiSans,
          'whitespace-pre-wrap text-pretty',
          loud
            ? 'text-[17px] font-medium leading-relaxed text-[var(--marketing-text)]'
            : muted
              ? 'text-[15px] leading-relaxed text-[var(--yeshuman-ink-subtle,var(--marketing-muted))]'
              : 'text-[15px] leading-relaxed text-[var(--marketing-text)]',
        )}
      >
        {value || '—'}
      </p>
    </section>
  )
}

export function WorkingSessionTableView({
  table,
  packet = false,
}: {
  table: WorkingSessionTable
  packet?: boolean
}) {
  const layerCards = new Map(table.layers.map((card) => [card.layer, card]))
  const frame = table.frame
  const map = table.map

  return (
    <div className="grid gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
      <div className="space-y-8">
        <p className={cn(yeshumanAnn, yeshumanAnnDim)}>{packet ? 'What we heard' : 'Brief'}</p>
        <BriefBlock label="Note" value={table.note} muted packet={packet} />
        <BriefBlock label="Problem" value={table.problem} packet={packet} />
        <BriefBlock label="Context" value={table.context} packet={packet} />
        <BriefBlock label="Requirements" value={table.requirements} loud packet={packet} />
        <BriefBlock label="What exists" value={table.exists} packet={packet} />
        <BriefBlock label="Their decision" value={table.decision} packet={packet} />
      </div>
      <div className="space-y-10">
        <div className="space-y-5">
          <p className={cn(yeshumanAnn, yeshumanAnnDim)}>Five layers</p>
          <ol className="space-y-5">
            {WORKING_SESSION_LAYERS.map((layer) => {
              const card = layerCards.get(layer)
              if (!card?.claim && !card?.fig) {
                return packet ? null : (
                  <li key={layer} className="border-t border-[var(--marketing-border)] pt-3">
                    <p className={yeshumanEditorialTerm}>{layer}</p>
                  </li>
                )
              }
              return (
                <li key={layer} className="border-t border-[var(--marketing-border)] pt-3">
                  <p className={yeshumanEditorialTerm}>{layer}</p>
                  {card.fig ? <p className={cn(yeshumanAnn, yeshumanAnnDim, 'mt-1')}>{card.fig}</p> : null}
                  {card.claim ? (
                    <p className={cn(yeshumanEditorialSm, 'mt-2 max-w-[36ch]')}>{card.claim}</p>
                  ) : null}
                </li>
              )
            })}
          </ol>
        </div>
        {map ? (
          <div className="space-y-3">
            <p className={cn(yeshumanAnn, yeshumanAnnDim)}>System map</p>
            {map.caption ? <p className={yeshumanEditorialSm}>{map.caption}</p> : null}
            <ul className="space-y-1">
              {map.nodes.map((node) => (
                <li key={node.id} className={cn(yeshumanUiSans, 'text-[15px]')}>
                  {node.label}
                </li>
              ))}
            </ul>
            {map.edges.length > 0 ? (
              <ul className={cn(yeshumanAnnDim, 'space-y-1 text-[13px]')}>
                {map.edges.map((edge) => (
                  <li key={`${edge.from}-${edge.to}`}>
                    {edge.from} → {edge.to}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {frame ? (
          <div className="space-y-3">
            <p className={cn(yeshumanAnn, yeshumanAnnDim)}>Frame</p>
            <dl className="space-y-2">
              {frame.surface_name ? <FrameRow label="Surface" value={frame.surface_name} /> : null}
              {frame.who ? <FrameRow label="Who" value={frame.who} /> : null}
              {frame.primary_action ? (
                <FrameRow label="Primary action" value={frame.primary_action} />
              ) : null}
              {frame.chrome ? <FrameRow label="Chrome" value={frame.chrome} /> : null}
              {frame.work ? <FrameRow label="Work" value={frame.work} /> : null}
              {frame.decision ? <FrameRow label="Decision" value={frame.decision} /> : null}
              {frame.first_slice_candidate ? (
                <FrameRow label="Possible first slice" value={frame.first_slice_candidate} />
              ) : null}
              {frame.not_this_session ? (
                <FrameRow label="Not this session" value={frame.not_this_session} />
              ) : null}
            </dl>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function FrameRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className={cn(yeshumanAnn, yeshumanAnnDim)}>{label}</dt>
      <dd className={cn(yeshumanUiSans, 'mt-1 text-[15px]')}>{value}</dd>
    </div>
  )
}
